import { useState, useEffect, useCallback } from 'react';
import { Box, Container, Paper } from '@mui/material';
import { useNavigate } from 'react-router';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { historyApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess, globalNotifyError } from 'views/utilities/AlertToast';

// Subcomponents
import TransactionHistoryBanner from './components/transaction-history/TransactionHistoryBanner';
import TransactionHistoryStats from './components/transaction-history/TransactionHistoryStats';
import TransactionHistoryToolbar from './components/transaction-history/TransactionHistoryToolbar';
import TransactionHistoryTable from './components/transaction-history/TransactionHistoryTable';

const TransactionHistory = () => {
    const navigate = useNavigate();

    // ─── Default Date & Cycle Setup ──────────────────────────────────
    // Usage period defaults to last month, repayment to current month
    const defaultMonth = moment().subtract(1, 'month').month() + 1;
    const defaultYear = moment().subtract(1, 'month').year();
    const defaultUsageStart = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    const defaultUsageEnd = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
    const defaultRepayStart = moment().startOf('month').format('YYYY-MM-DD');
    const defaultRepayEnd = moment().endOf('month').format('YYYY-MM-DD');

    // ─── Filter States ────────────────────────────────────────────────
    const [month, setMonth] = useState(defaultMonth);
    const [year, setYear] = useState(defaultYear);
    const [usageFromDate, setUsageFromDate] = useState(defaultUsageStart);
    const [usageToDate, setUsageToDate] = useState(defaultUsageEnd);
    const [repaymentFromDate, setRepaymentFromDate] = useState(defaultRepayStart);
    const [repaymentToDate, setRepaymentToDate] = useState(defaultRepayEnd);

    const [billPaidStatus, setBillPaidStatus] = useState('all');

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // ─── Pagination States (Backend 1-indexed, MUI TablePagination 0-indexed)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // ─── Data & Loading States ────────────────────────────────────────
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // ─── Debounce Search Term (350ms) ─────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // ─── Fetch Transactions API ───────────────────────────────────────
    const fetchTransactionHistory = useCallback(
        async (overrides = {}) => {
            setLoading(true);
            try {
                const curPage = overrides.page !== undefined ? overrides.page : page;
                const curPerPage = overrides.rowsPerPage !== undefined ? overrides.rowsPerPage : rowsPerPage;
                const curMonth = overrides.month !== undefined ? overrides.month : month;
                const curYear = overrides.year !== undefined ? overrides.year : year;
                const curUsageFrom = overrides.usageFromDate !== undefined ? overrides.usageFromDate : usageFromDate;
                const curUsageTo = overrides.usageToDate !== undefined ? overrides.usageToDate : usageToDate;
                const curRepayFrom = overrides.repaymentFromDate !== undefined ? overrides.repaymentFromDate : repaymentFromDate;
                const curRepayTo = overrides.repaymentToDate !== undefined ? overrides.repaymentToDate : repaymentToDate;
                const curStatus = overrides.billPaidStatus !== undefined ? overrides.billPaidStatus : billPaidStatus;
                const curSearch = overrides.search !== undefined ? overrides.search : debouncedSearch;

                const payload = {
                    month: Number(curMonth),
                    year: Number(curYear),
                    usage_from_date: curUsageFrom,
                    usage_to_date: curUsageTo,
                    repayment_from_date: curRepayFrom,
                    repayment_to_date: curRepayTo,
                    bill_paid_status: curStatus,
                    page: curPage + 1,
                    per_page: curPerPage
                };

                // Optional search across name, mobile, email, RRN, ID
                if (curSearch && String(curSearch).trim()) {
                    payload.search = String(curSearch).trim();
                }

                const response = await historyApi(payload);
                const resData = response?.data;

                let list = [];
                let count = 0;

                if (resData?.status === true || resData?.success === true || response?.status === 200) {
                    if (Array.isArray(resData?.data)) {
                        list = resData.data;
                    } else if (Array.isArray(resData?.data?.data)) {
                        list = resData.data.data;
                    } else if (Array.isArray(resData?.records)) {
                        list = resData.records;
                    } else if (Array.isArray(resData)) {
                        list = resData;
                    }

                    count = resData?.pagination?.total ?? resData?.data?.total ?? resData?.total ?? resData?.total_count ?? list.length;

                    setSummary(resData?.summary || null);
                }

                setTransactions(Array.isArray(list) ? list : []);
                setTotalCount(Number(count) || (Array.isArray(list) ? list.length : 0));
            } catch (error) {
                console.error('Failed to fetch transaction history:', error);
                setTransactions([]);
                setTotalCount(0);
                setSummary(null);
                handleCatchError(error);
            } finally {
                setLoading(false);
            }
        },
        [page, rowsPerPage, month, year, usageFromDate, usageToDate, repaymentFromDate, repaymentToDate, billPaidStatus, debouncedSearch]
    );

    // Fetch on pagination or search change
    useEffect(() => {
        fetchTransactionHistory();
    }, [page, rowsPerPage, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Month & Year 1-Click Cycle Filter Handler ────────────────────
    const handleMonthYearChange = (newMonth, newYear) => {
        setMonth(newMonth);
        setYear(newYear);

        const usageStart = moment({ year: newYear, month: newMonth - 1 }).startOf('month');
        const usageEnd = moment({ year: newYear, month: newMonth - 1 }).endOf('month');
        const repayStart = moment(usageStart).add(1, 'month').startOf('month');
        const repayEnd = moment(usageStart).add(1, 'month').endOf('month');

        const uFrom = usageStart.format('YYYY-MM-DD');
        const uTo = usageEnd.format('YYYY-MM-DD');
        const rFrom = repayStart.format('YYYY-MM-DD');
        const rTo = repayEnd.format('YYYY-MM-DD');

        setUsageFromDate(uFrom);
        setUsageToDate(uTo);
        setRepaymentFromDate(rFrom);
        setRepaymentToDate(rTo);
        setPage(0);

        fetchTransactionHistory({
            month: newMonth,
            year: newYear,
            usageFromDate: uFrom,
            usageToDate: uTo,
            repaymentFromDate: rFrom,
            repaymentToDate: rTo,
            page: 0
        });
    };

    // ─── Filter & Search Handling ─────────────────────────────────────
    const handleApplyFilter = () => {
        setPage(0);
        fetchTransactionHistory({ page: 0 });
    };

    const handleResetFilter = () => {
        setMonth(defaultMonth);
        setYear(defaultYear);
        setUsageFromDate(defaultUsageStart);
        setUsageToDate(defaultUsageEnd);
        setRepaymentFromDate(defaultRepayStart);
        setRepaymentToDate(defaultRepayEnd);
        setBillPaidStatus('all');
        setSearchTerm('');
        setDebouncedSearch('');
        setPage(0);

        fetchTransactionHistory({
            month: defaultMonth,
            year: defaultYear,
            usageFromDate: defaultUsageStart,
            usageToDate: defaultUsageEnd,
            repaymentFromDate: defaultRepayStart,
            repaymentToDate: defaultRepayEnd,
            billPaidStatus: 'all',
            search: '',
            page: 0
        });
    };

    // ─── Export to Excel ──────────────────────────────────────────────
    const handleExportExcel = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            let exportList = transactions;

            try {
                const exportPayload = {
                    month: Number(month),
                    year: Number(year),
                    usage_from_date: usageFromDate,
                    usage_to_date: usageToDate,
                    repayment_from_date: repaymentFromDate,
                    repayment_to_date: repaymentToDate,
                    bill_paid_status: billPaidStatus,
                    page: 1,
                    per_page: Math.max(totalCount || 500, 500)
                };

                if (debouncedSearch && debouncedSearch.trim()) {
                    exportPayload.search = debouncedSearch.trim();
                }

                const res = await historyApi(exportPayload);
                const resData = res?.data;
                if (Array.isArray(resData?.data)) exportList = resData.data;
                else if (Array.isArray(resData?.data?.data)) exportList = resData.data.data;
                else if (Array.isArray(resData?.records)) exportList = resData.records;
            } catch (err) {
                console.warn('Fallback to currently loaded transactions for export:', err);
            }

            if (!exportList || exportList.length === 0) {
                globalNotifyError('No repayment records available to export for this cycle');
                return;
            }

            const rows = exportList.map((item) => ({
                'Employee ID': item.crossbanking_id ?? item.id ?? '',
                'Employee Name': item.crossbanking_name ?? item.name ?? '',
                Mobile: item.mobileno ?? item.mobile ?? '',
                Email: item.email || '',
                'Recharge (₹)': Number(item.recharge || 0),
                'Bill Payment (₹)': Number(item.billpayment || 0),
                'Total Used (₹)': Number(item.totalused ?? item.amount ?? 0),
                'Repayment Settled (₹)': Number(item.credit_repayment ?? item.paid_amount ?? 0),
                'Latest Repayment (₹)':
                    item.latest_credit_repayment !== null && item.latest_credit_repayment !== undefined
                        ? Number(item.latest_credit_repayment)
                        : '',
                'Repayment Date': item.credit_repayment_date
                    ? moment(item.credit_repayment_date).format('YYYY-MM-DD HH:mm:ss')
                    : item.settled_at || '',
                'Bank RRN': item.rrn || '',
                'Post-Repayment Amount (₹)': Number(item.total_used_after_repayment ?? item.billpayment_after_repayment ?? 0)
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            worksheet['!cols'] = [
                { wch: 16 }, // Employee ID
                { wch: 26 }, // Employee Name
                { wch: 16 }, // Mobile
                { wch: 28 }, // Email
                { wch: 16 }, // Recharge
                { wch: 16 }, // Bill Payment
                { wch: 16 }, // Total Used
                { wch: 22 }, // Repayment Settled
                { wch: 20 }, // Latest Repayment
                { wch: 22 }, // Repayment Date
                { wch: 24 }, // Bank RRN
                { wch: 26 } // Post-Repayment Amount
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Repayments');
            XLSX.writeFile(workbook, `Repayment_History_${usageFromDate}_to_${repaymentToDate}.xlsx`);
            globalNotifySuccess('Repayment history exported to Excel successfully');
        } catch (error) {
            console.error('Failed to export repayment history:', error);
            handleCatchError(error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 8 }}>
            {/* Header Banner */}
            <TransactionHistoryBanner onBack={() => navigate('/HR/credit-limits')} />

            {/* Main Content Container */}
            <Container maxWidth="xl" sx={{ mt: -5 }}>
                {/* Stats Summary Cards */}
                <TransactionHistoryStats summary={summary} totalCount={totalCount} transactions={transactions} />

                {/* Table & Controls Card */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                    }}
                >
                    {/* Filters Toolbar */}
                    <TransactionHistoryToolbar
                        month={month}
                        year={year}
                        onMonthYearChange={handleMonthYearChange}
                        usageFromDate={usageFromDate}
                        usageToDate={usageToDate}
                        repaymentFromDate={repaymentFromDate}
                        repaymentToDate={repaymentToDate}
                        billPaidStatus={billPaidStatus}
                        onBillPaidStatusChange={setBillPaidStatus}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        onApplyFilter={handleApplyFilter}
                        onResetFilter={handleResetFilter}
                        onExport={handleExportExcel}
                        isExporting={isExporting}
                        exportDisabled={transactions.length === 0}
                        onRefresh={() => fetchTransactionHistory()}
                        loading={loading}
                    />

                    {/* Table with Pagination */}
                    <TransactionHistoryTable
                        loading={loading}
                        transactions={transactions}
                        totalCount={totalCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                            fetchTransactionHistory({ page: newPage });
                        }}
                        onRowsPerPageChange={(newPerPage) => {
                            setRowsPerPage(newPerPage);
                            setPage(0);
                            fetchTransactionHistory({ page: 0, rowsPerPage: newPerPage });
                        }}
                        usageFromDate={usageFromDate}
                        repaymentToDate={repaymentToDate}
                        onResetFilter={handleResetFilter}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default TransactionHistory;
